// 移植自 crm/src/SideMenu.jsx（桌面側邊選單，md 以上顯示）。選單項目在 ./menuItems.js，與 DrawerMenu 共用。
// ≤780px 自動收合、>780px 自動展開；手動切換仍有效，直到下次跨越 780px 重新套用。收合時 hover（或鍵盤焦點）顯示項目名稱。
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'react-router'
import { access } from '@/lib/auth'
import { t } from '@/i18n'
import { AppLink } from '@/components/ui/AppLink'
import { CollapseIcon } from '@/components/ui/icons'
import { MAIN_MENU, SYSTEM_MENU, BADGE_STYLE, isMenuActive } from './menuItems'
import { useMenuBadges } from './useMenuBadges'

const narrow = window.matchMedia('(max-width: 780px)')

function NavItem({ item, collapsed, active, badge, onTipShow, onTipHide }) {
  const Icon = item.icon
  const label = t('app', item.label)
  const badgeStyle = badge ? BADGE_STYLE[item.badge] : null
  return (
    <li className={collapsed ? '' : 'w-full'}>
      <AppLink
        href={item.href}
        onMouseEnter={(e) => onTipShow(e, label)}
        onMouseLeave={onTipHide}
        // 鍵盤焦點時也顯示名稱（2026-10-13 拍板）；滑鼠點擊的焦點不觸發
        onFocus={(e) => e.currentTarget.matches(':focus-visible') && onTipShow(e, label)}
        onBlur={onTipHide}
        className={`relative flex items-center rounded-brand-large p-2.5 text-sm font-normal leading-5 transition-colors ${collapsed ? 'justify-center' : 'w-full gap-2'} ${active ? 'bg-brand-primary text-white' : 'text-brand-black-700 hover:bg-brand-primary-hover hover:text-white'}`}
      >
        <span className="flex size-5 shrink-0 items-center justify-center">
          <Icon className="size-5" />
        </span>
        {!collapsed && <span className="whitespace-nowrap">{label}</span>}
        {badgeStyle && (
          <span aria-label="status" className={`${badgeStyle.size} shrink-0 rounded-full ${badgeStyle.className} ${collapsed ? 'absolute right-1.5 top-1.5' : 'ml-auto'}`} />
        )}
      </AppLink>
    </li>
  )
}

export function SideMenu() {
  const { pathname } = useLocation()
  const badges = useMenuBadges()
  const [collapsed, setCollapsed] = useState(narrow.matches)
  const [tip, setTip] = useState({ text: '', x: 0, y: 0, show: false })

  useEffect(() => {
    const onChange = (e) => setCollapsed(e.matches)
    narrow.addEventListener('change', onChange)
    return () => narrow.removeEventListener('change', onChange)
  }, [])

  const onTipShow = (e, text) => {
    if (!collapsed) return
    const rect = e.currentTarget.getBoundingClientRect()
    setTip({ text, x: rect.right + 8, y: rect.top + rect.height / 2, show: true })
  }
  const onTipHide = () => setTip((prev) => ({ ...prev, show: false }))

  const renderItems = (items) =>
    items
      .filter((item) => !item.perms || access(...item.perms))
      .map((item) => (
        <NavItem
          key={item.href}
          item={item}
          collapsed={collapsed}
          active={isMenuActive(pathname, item)}
          badge={item.badge ? badges[item.badge] : false}
          onTipShow={onTipShow}
          onTipHide={onTipHide}
        />
      ))

  return (
    <>
      <aside className={`relative hidden flex-none flex-col transition-all duration-300 ease-in-out md:flex ${collapsed ? 'w-[66px]' : 'w-[200px]'}`}>
        {/* overflow-x-hidden + 各文字 nowrap：展開動畫期間寬度不足時文字被裁切揭開，而非擠壓換行 */}
        <div className="absolute h-full w-full overflow-x-hidden overflow-y-auto scrollbar-none px-3 py-6">
          <div className={`flex h-10 items-center rounded-brand-base ${collapsed ? 'justify-center' : 'justify-between pl-2.5 pr-2'}`}>
            {!collapsed && <span className="whitespace-nowrap text-xs font-medium leading-[18px] text-brand-black-500">{t('app', 'System Directory')}</span>}
            <button type="button" className="flex size-[26px] shrink-0 items-center justify-center rounded-full text-brand-black-500 transition-colors hover:bg-brand-black-100" onClick={() => setCollapsed((v) => !v)}>
              <CollapseIcon className={`h-4 ${collapsed ? '-scale-x-100' : ''}`} />
            </button>
          </div>
          <ul className={`flex flex-col gap-1 ${collapsed ? 'items-center' : ''}`}>{renderItems(MAIN_MENU)}</ul>

          {!collapsed && <h2 className="mb-1 mt-4 whitespace-nowrap px-2.5 text-xs font-medium leading-[18px] text-brand-black-500">{t('app', 'System')}</h2>}
          <ul className={`flex flex-col gap-1 ${collapsed ? 'items-center mt-1' : ''}`}>{renderItems(SYSTEM_MENU)}</ul>
        </div>
      </aside>

      {createPortal(
        <div
          className={`sidemenu-tooltip ${tip.show ? 'show' : ''}`}
          style={{ left: `${tip.x}px`, top: `${tip.y}px`, transform: `translateY(-50%) ${tip.show ? 'translateX(0)' : 'translateX(-4px)'}` }}
        >
          {tip.text}
        </div>,
        document.body,
      )}
    </>
  )
}
